import React, { useCallback, useEffect, useState } from 'react';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { ConfirmModal } from '../../../../shared/ui/ConfirmModal';
import type { Provider } from '../../../../entities/provider';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { ProviderModal } from '../../../../features/admin-provider-form';

const maskKey = (key: string): string => key ? '••••••••••••••••' : '—';

const ProvidersSection: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; provider?: Provider } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Provider | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadProviders = useCallback(() => {
    setLoading(true);
    pb.collection('provides')
      .getFullList<Provider>({ sort: 'name', fields: 'id,name,key,type,data,actual,created,updated' })
      .then(setProviders)
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const handleDelete = useCallback((provider: Provider) => {
    setDeletingId(provider.id);
    pb.collection('provides')
      .delete(provider.id)
      .then(() => setProviders(prev => prev.filter(p => p.id !== provider.id)))
      .finally(() => setDeletingId(null));
  }, []);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Провайдеры</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : `${providers.length} провайдеров`}</p>
        </div>
        <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
          <IconPlus size={15} stroke={2.5} />Добавить
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-ut-wrap">
          <table className="adm-ut">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Ключ</th>
                <th>Доп. данные</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {providers.map(provider => (
                <tr key={provider.id} className="adm-ut-row">
                  <td>
                    <span className="adm-ut-name">{provider.name || '—'}</span>
                  </td>
                  <td>
                    <span className="adm-role-badge user">{provider.type}</span>
                  </td>
                  <td>
                    <span className="adm-ut-email" style={{ fontFamily: 'monospace' }}>
                      {maskKey(provider.key)}
                    </span>
                  </td>
                  <td>
                    {provider.type === 'P1SMS' && provider.data?.phone ? (
                      <span className="adm-ut-email">{provider.data.phone}</span>
                    ) : (
                      <span className="adm-ut-email">—</span>
                    )}
                  </td>
                  <td className="adm-ut-actions-cell">
                    <div className="adm-ut-actions">
                      <Button
                        variant="adm-edit"
                        type="button"
                        title="Редактировать"
                        onClick={() => setModal({ mode: 'edit', provider })}
                      >
                        <IconPencil size={13} stroke={2} />
                      </Button>
                      <Button
                        variant="adm-delete"
                        type="button"
                        title="Удалить"
                        disabled={deletingId === provider.id}
                        onClick={() => setConfirmDelete(provider)}
                      >
                        <IconTrash size={13} stroke={2} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {providers.length === 0 && (
                <tr><td colSpan={5} className="adm-empty">Провайдеры не найдены</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ProviderModal
          mode={modal.mode}
          initial={modal.provider ?? null}
          onClose={() => setModal(null)}
          onSaved={updated => {
            if (updated) {
              setProviders(prev =>
                modal.mode === 'edit'
                  ? prev.map(p => p.id === updated.id ? updated : p)
                  : [...prev, updated].sort((a, b) => a.name.localeCompare(b.name))
              );
            }
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Удалить провайдера?"
          content={<>Вы уверены, что хотите удалить <strong>{confirmDelete.name}</strong>? Это действие необратимо.</>}
          onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export { ProvidersSection };
